import { BetterInputNumber } from '@/shared/BetterInputNumber'
import { MqttCombination, mqttVariables } from '@/shared/DefaultValues'
import {
  getOptions,
  mqttFunctionItems,
  mqttVariableType
} from '@/shared/SelectData'
import { SelectItem } from '@/shared/SelectItem'
import {
  TemplateTransferConnectionProps,
  TransferFormatItem,
  TransferFunctionItem
} from '@/template/interface'
import { Guid } from '@/utils/guid'
import { getFormatMessage } from '@/utils/utils'
import { ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs
} from 'antd'
import React, { useState } from 'react'
import { DataType, EditableTable } from './EditableTable'
import { TopicType } from './Variable'

const { TabPane } = Tabs
const { confirm } = Modal

export const TemplateTransferConnection: React.FC<TemplateTransferConnectionProps> =
  (props: TemplateTransferConnectionProps) => {
    const [visible, setVisible] = useState<boolean>(false)
    const [item, setItem] = useState<TransferFunctionItem>({})
    const [pubData, setPubData] = useState<DataType[]>([])
    const [subData, setSubData] = useState<DataType[]>([])
    const [selectTab, setSelectTab] = useState<string>('pub')
    const [selectFunc, setSelectFunc] = useState<string>(
      mqttFunctionItems[0].value
    )
    const [form] = Form.useForm<TransferFunctionItem>()
    const [searchForm] = Form.useForm()
    const formatMessage = getFormatMessage()
    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 8 }
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 14 }
      }
    }
    const columns = [
      {
        title: formatMessage({ id: 'app.template.transfer.connection.index' }),
        key: 'app.template.transfer.connection.index',
        render: (text: any, record: any, index: any) => index + 1
      },
      {
        title: formatMessage({ id: 'app.template.transfer.connection.name' }),
        key: 'app.template.transfer.connection.name',
        dataIndex: 'name'
      },
      {
        title: formatMessage({
          id: 'app.template.transfer.connection.function'
        }),
        key: 'app.template.transfer.connection.function',
        dataIndex: 'function',
        render: (text: any, record: TransferFunctionItem, index: any) =>
          formatMessage({ id: `app.mqtt.function.${record.function}` })
      },
      {
        title: formatMessage({
          id: 'app.template.transfer.connection.operation'
        }),
        key: 'app.template.transfer.connection.operation',
        render: (text: any, record: any) => (
          <Space size='middle'>
            <a
              onClick={() => {
                handleCreate(record)
              }}
            >
              {formatMessage({
                id: 'app.template.transfer.connection.operation.edit'
              })}
            </a>
            <a
              onClick={() => {
                confirm({
                  title: formatMessage({ id: 'app.modal.confirm.delete' }),
                  icon: <ExclamationCircleOutlined />,
                  okText: formatMessage({ id: 'app.modal.ok' }),
                  cancelText: formatMessage({ id: 'app.modal.cancel' }),
                  onOk() {
                    if (props.onDelete) {
                      props.onDelete(record)
                    }
                  }
                })
              }}
            >
              {formatMessage({
                id: 'app.template.transfer.connection.operation.delete'
              })}
            </a>
          </Space>
        )
      }
    ]

    const getFormatItems = (
      items: DataType[],
      set: Set<string>
    ): TransferFormatItem[] => {
      const formatItems: TransferFormatItem[] = []
      const recursion = (
        parent: TransferFormatItem[],
        treeItems: DataType[]
      ) => {
        for (const item of treeItems) {
          let type = ''
          let combinationId = item.combinationId
          if (item.valueType == 'object' || item.valueType == 'array') {
            type = item.valueType
            combinationId = null
          } else if (item.relateName && item.variableId) {
            type = 'item'
          } else if (item.relateName && !item.variableId) {
            type = 'attri'
          } else {
            type = 'define_attri'
          }
          const formatItem: TransferFormatItem = {
            id: item.key.toString(),
            obj_type: type,
            relate_name: item.relateName,
            anonymous_name: item.anonymousName,
            value_type: item.valueType,
            item_id: item.variableId || null,
            default_value: item.defaultValue || null,
            combe_type: combinationId,
            children: null
          }
          parent.push(formatItem)
          if (item.combinationId) {
            set.add(item.combinationId)
          }

          if (item.children && item.children.length > 0) {
            formatItem.children = []
            recursion(formatItem.children, item.children)
          }
        }
      }
      recursion(formatItems, items)
      return formatItems
    }

    const getDataTypes = (items: TransferFormatItem[]): DataType[] => {
      const dataTypes: DataType[] = []
      const recursion = (
        parent: DataType[],
        treeItems: TransferFormatItem[]
      ) => {
        for (const item of treeItems) {
          // 1.查找变量表
          let variable = props.variables?.find((x) => x.id === item.item_id)
          if (!variable) {
            // 2. 如果变量表不存在，则查找内置变量表
            if (item.relate_name && item.relate_name in mqttVariables) {
              variable = mqttVariables[item.relate_name]
            } else {
              // 3. 如果内置变量也不存在，则查找报警表
              const alarm = props.alarms?.find((x) => x.id === item.item_id)
              if (alarm) {
                const alarmVar = props.variables?.find(
                  (x) => x.id === alarm.variableId
                )
                if (alarmVar) {
                  // 克隆对象，防止修改名称时，将原始对象修改(jira-FBOX-3728)
                  variable = { ...alarmVar }
                  variable.name = alarm.name
                }
              }
            }
          }

          // 编辑MQTT变量时也需要判断按位索引(jira-FBOX-3581)
          const dataTypes: SelectItem[] = variable?.enabledIndex
            ? mqttVariableType[0]
            : mqttVariableType[variable?.dataType!]

          const dataType: DataType = {
            key: item.id || '',
            valueType: item.value_type,
            anonymousName: item.anonymous_name,
            defaultValue: item.default_value,
            valueTypeItems: dataTypes,
            combinationId: item.combe_type,
            variableId: item.item_id,
            variableType: variable?.dataType,
            variableName: variable?.name,
            relateName: item.relate_name
          }
          parent.push(dataType)
          if (item.children && item.children.length > 0) {
            dataType.children = []
            recursion(dataType.children, item.children)
          }
        }
      }
      recursion(dataTypes, items)
      return dataTypes
    }

    const updateData = (item?: TransferFunctionItem) => {
      if (item) {
        if (item.pub && item.pub.format && item.pub.format.length > 0) {
          const items: DataType[] = getDataTypes(item.pub.format)
          setPubData(items)
        } else {
          setPubData([])
        }
        if (item.sub && item.sub.format && item.sub.format.length > 0) {
          const arr: DataType[] = getDataTypes(item.sub.format)
          setSubData(arr)
        } else {
          setSubData([])
        }
        const func = item.function || ''
        setItem(item)
        setSelectFunc(func)
        form.setFieldsValue(item)
      } else {
        setItem({})
        setPubData([])
        setSubData([])
        setSelectTab('pub')
        setSelectFunc(mqttFunctionItems[0].value)
        form.setFieldsValue({
          function: 'PushMonitorData',
          pub: {
            topic: '$sn/'
          },
          sub: {
            topic: '$sn/'
          }
        })
      }
    }

    const handleCreate = (item?: TransferFunctionItem) => {
      form.resetFields()
      setVisible(true)
      updateData(item)
    }

    const handleEditModalCancel = () => {
      setVisible(false)
    }

    const handlePubDataChange = (data: DataType[]) => {
      setPubData(data)
    }

    const handleSubDataChange = (
      data: DataType[],
      combination?: MqttCombination
    ) => {
      setSubData(data)
    }

    const handleEditModalOk = () => {
      form
        .validateFields()
        .then((values: TransferFunctionItem) => {
          const merged = Object.assign(item, values)
          const set = new Set<string>()
          if (!merged.id) {
            merged.id = Guid.raw()
          }
          if (props.onFormValidate) {
            if (!props.onFormValidate(true, merged)) return
          }

          if (merged.pub) {
            merged.pub.format = getFormatItems(pubData, set)
          }
          if (merged.sub) {
            merged.sub.format = getFormatItems(subData, set)
          }
          if (props.onFormSubmit) {
            const combinations: MqttCombination[] = []
            set.forEach((x) => {
              const c = props.combinations?.find((y) => y.id == x)
              if (c) {
                combinations.push(c)
              }
            })
            props.onFormSubmit(merged, combinations)
          }
          form.resetFields()
          setVisible(false)
        })
        .catch((info) => {
          console.log('Validate Failed:', info)
        })
    }

    const handleReset = () => {
      searchForm.resetFields()
      if (props.onSearch) {
        props.onSearch(searchForm.getFieldsValue())
      }
    }

    const handleSearch = () => {
      const value = searchForm.getFieldsValue()
      if (props.onSearch) {
        props.onSearch(value)
      }
    }

    const editTable = (topicType: TopicType) => (
      <EditableTable
        data={topicType == 'pub' ? pubData : subData}
        topicType={topicType}
        selectFunc={selectFunc}
        alarms={props.alarms}
        variables={props.variables}
        combinations={props.combinations}
        enableCache={topicType == 'pub' ? props.enableCache : false}
        onDataChange={
          topicType == 'pub' ? handlePubDataChange : handleSubDataChange
        }
      />
    )

    return (
      <div>
        <Row style={{ marginBottom: 16 }}>
          <Col span={20}>
            <Form form={searchForm} layout='inline'>
              <Form.Item name='name'>
                <Input
                  allowClear={true}
                  placeholder={formatMessage({
                    id: 'app.template.transfer.connection.search.name'
                  })}
                />
              </Form.Item>
              <Form.Item name='function'>
                <Select
                  allowClear={true}
                  placeholder={formatMessage({
                    id: 'app.template.transfer.connection.search.function'
                  })}
                  style={{ width: 120 }}
                >
                  {getOptions(mqttFunctionItems)}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button onClick={handleReset} type='link'>
                  {formatMessage({
                    id: 'app.template.transfer.connection.search.clean'
                  })}
                </Button>
                <Button
                  onClick={handleSearch}
                  type='primary'
                  icon={<SearchOutlined />}
                >
                  {formatMessage({
                    id: 'app.template.transfer.connection.search'
                  })}
                </Button>
              </Form.Item>
            </Form>
          </Col>

          <Col span={4}>
            <Space align='end'>
              <Button type='primary' onClick={() => handleCreate()}>
                {formatMessage({
                  id: 'app.template.transfer.connection.create'
                })}
              </Button>
            </Space>
          </Col>
        </Row>

        <Table rowKey='id' columns={columns} dataSource={props.data} />

        <Modal
          destroyOnClose={true}
          visible={visible}
          onCancel={handleEditModalCancel}
          onOk={handleEditModalOk}
          title={
            item.id
              ? formatMessage({
                  id: 'app.template.transfer.connection.create.edit',
                  parameters: { name: item.name }
                })
              : formatMessage({
                  id: 'app.template.transfer.connection.create'
                })
          }
          width={900}
          maskClosable={false}
          okText={formatMessage({ id: 'app.modal.ok' })}
          cancelText={formatMessage({ id: 'app.modal.cancel' })}
        >
          <Form form={form} {...formItemLayout}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.connection.name'
                  })}
                  name='name'
                  rules={[
                    {
                      required: true,
                      transform: (value) => value?.trim(),
                      message: formatMessage({
                        id: formatMessage({
                          id: 'app.template.transfer.connection.name.message'
                        })
                      })
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.transfer.connection.function'
                  })}
                  name='function'
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.template.transfer.connection.function.message'
                      })
                    }
                  ]}
                  labelCol={{
                    xs: { span: 24 },
                    sm: { span: 4 }
                  }}
                >
                  <Select
                    onChange={(value: string) => {
                      form.setFieldsValue({
                        alarm_event: { before_time: 10, after_time: 5 }
                      })
                      setSelectFunc(value)
                    }}
                  >
                    {getOptions(mqttFunctionItems)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {selectFunc === 'PushAlarmEvent' && (
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={formatMessage({
                      id: 'app.template.transfer.connection.alarmEvent'
                    })}
                    name={['alarm_event', 'item_id']}
                    rules={[
                      {
                        required: true,
                        message: formatMessage({
                          id: formatMessage({
                            id: 'app.template.transfer.connection.alarmEvent.message'
                          })
                        })
                      }
                    ]}
                  >
                    <Select
                      showSearch
                      filterOption={(input, option) =>
                        option!.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {props.alarms?.map((x) => (
                        <Select.Option value={x.id!} key={x.id}>
                          {x.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex' }}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.transfer.connection.alarmEvent.time'
                      })}
                      name={['alarm_event', 'before_time']}
                      style={{ width: '50%' }}
                      rules={[
                        {
                          required: true,
                          message: formatMessage({
                            id: formatMessage({
                              id: 'app.template.transfer.connection.alarmEvent.time.message'
                            })
                          })
                        }
                      ]}
                      wrapperCol={{
                        xs: { span: 24 },
                        sm: { span: 15 }
                      }}
                    >
                      <BetterInputNumber
                        addonBefore={formatMessage({
                          id: 'app.template.transfer.connection.alarmEvent.before'
                        })}
                        addonAfter={formatMessage({
                          id: 'app.template.transfer.connection.alarmEvent.unit'
                        })}
                        min={0}
                        max={20}
                      />
                    </Form.Item>
                    <Form.Item
                      name={['alarm_event', 'after_time']}
                      style={{ width: '50%' }}
                      rules={[
                        {
                          required: true,
                          message: formatMessage({
                            id: formatMessage({
                              id: 'app.template.transfer.connection.alarmEvent.time.message'
                            })
                          })
                        }
                      ]}
                      wrapperCol={{
                        xs: { span: 24 },
                        sm: { span: 15 }
                      }}
                    >
                      <BetterInputNumber
                        addonBefore={formatMessage({
                          id: 'app.template.transfer.connection.alarmEvent.after'
                        })}
                        addonAfter={formatMessage({
                          id: 'app.template.transfer.connection.alarmEvent.unit'
                        })}
                        min={0}
                        max={20}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            )}

            <Tabs destroyInactiveTabPane={false} defaultActiveKey={selectTab}>
              <TabPane
                tab={formatMessage({
                  id: 'app.template.transfer.connection.pub'
                })}
                key='pub'
                forceRender={true}
              >
                {editTable('pub')}
              </TabPane>
              <TabPane
                tab={formatMessage({
                  id: 'app.template.transfer.connection.sub'
                })}
                key='sub'
                forceRender={true}
              >
                {editTable('sub')}
              </TabPane>
            </Tabs>
          </Form>
        </Modal>
      </div>
    )
  }
